/**
 * Activity Heatmap — GitHub-style SVG contribution graph.
 * Shows overall activity across ALL habits for the year.
 *
 * - 5 intensity levels (blue scale) based on activity_count / maxCount
 * - ISO weeks: Monday start (consistent with O'zbekiston + odat heatmap)
 * - Month labels with gaps between month blocks
 * - Tooltip on each cell
 * - Legend: "Kam ←→ Ko'p"
 */

import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  getISODay, startOfMonth, endOfMonth, eachDayOfInterval, format,
} from 'date-fns'
import { uz } from 'date-fns/locale'
import { useActivityHeatmap } from '@turnifly/shared/hooks'
import { Skeleton } from '@turnifly/shared/ui'

interface ActivityHeatmapProps {
  userId: string
  year: number
}

const CELL = 11
const GAP = 2
const STEP = CELL + GAP
const DAY_LABEL_W = 22
const MONTH_GAP = 6

const INTENSITY_COLORS = [
  'rgba(255,255,255,0.04)',   // 0: empty
  'rgba(0,97,255,0.25)',      // 1: low
  'rgba(0,97,255,0.45)',      // 2: medium-low
  'rgba(0,97,255,0.65)',      // 3: medium-high
  'rgba(0,97,255,0.9)',       // 4: high
] as const

const WEEKDAYS_UZ = ['Du', 'Se', 'Ch', 'Pa', 'Ju', 'Sh', 'Ya']
const WEEKDAYS_EN = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']

interface MonthBlock {
  label: string
  weeks: (Date | null)[][] // 7 rows (Mon=0..Sun=6) × N cols
}

export const ActivityHeatmap: React.FC<ActivityHeatmapProps> = ({ userId, year }) => {
  const { t, i18n } = useTranslation()
  const { data: heatmapData, isLoading } = useActivityHeatmap(userId, year)
  const locale = i18n.language === 'uz' ? uz : undefined

  const { activityMap, maxCount, monthBlocks, totalW, totalH } = useMemo(() => {
    const am = new Map<string, number>()
    let mx = 0
    for (const entry of heatmapData ?? []) {
      am.set(entry.activity_date, entry.activity_count)
      if (entry.activity_count > mx) mx = entry.activity_count
    }

    const today = new Date()
    const isCurrentYear = year === today.getFullYear()
    const blocks: MonthBlock[] = []

    for (let m = 0; m < 12; m++) {
      const mStart = startOfMonth(new Date(year, m, 1))
      const mEnd = isCurrentYear && m === today.getMonth()
        ? today
        : endOfMonth(new Date(year, m, 1))

      // Skip future months entirely
      if (mStart > today) continue

      const allDays = eachDayOfInterval({ start: mStart, end: mEnd })

      const cols: (Date | null)[][] = []
      let currentCol: (Date | null)[] = new Array(7).fill(null) as (Date | null)[]

      for (const day of allDays) {
        const row = getISODay(day) - 1 // 0=Mon, 6=Sun (ISO)
        if (row === 0 && currentCol.some((d) => d !== null)) {
          cols.push(currentCol)
          currentCol = new Array(7).fill(null) as (Date | null)[]
        }
        currentCol[row] = day
      }
      if (currentCol.some((d) => d !== null)) {
        cols.push(currentCol)
      }

      blocks.push({ label: format(mStart, 'LLL', { locale }), weeks: cols })
    }

    let tw = DAY_LABEL_W
    for (const block of blocks) {
      tw += block.weeks.length * STEP + MONTH_GAP
    }
    const th = 16 + 7 * STEP

    return { activityMap: am, maxCount: mx, monthBlocks: blocks, totalW: tw, totalH: th }
  }, [heatmapData, year, locale])

  if (isLoading) {
    return <Skeleton className="h-28 w-full rounded-xl" />
  }

  function getColor(count: number): string {
    if (count === 0 || maxCount === 0) return INTENSITY_COLORS[0]
    const ratio = count / maxCount
    if (ratio <= 0.25) return INTENSITY_COLORS[1]
    if (ratio <= 0.5) return INTENSITY_COLORS[2]
    if (ratio <= 0.75) return INTENSITY_COLORS[3]
    return INTENSITY_COLORS[4]
  }

  const weekdays = i18n.language === 'uz' ? WEEKDAYS_UZ : WEEKDAYS_EN

  return (
    <div>
      <div className="overflow-x-auto scrollbar-hide">
        <svg width={totalW} height={totalH} style={{ display: 'block', minWidth: totalW, flexShrink: 0 }}>
          {/* Weekday labels (ISO: Mon first) */}
          {weekdays.map((wd, i) => (
            <text key={`wd-${i}`} x={0} y={16 + i * STEP + CELL / 2 + 3} fill="#768c9e" fontSize={8} fontWeight={500}>
              {wd}
            </text>
          ))}

          {/* Month blocks with gaps */}
          {(() => {
            let offsetX = DAY_LABEL_W
            return monthBlocks.map((block, mi) => {
              const blockX = offsetX
              offsetX += block.weeks.length * STEP + MONTH_GAP

              return (
                <g key={`mb-${mi}`}>
                  <text x={blockX} y={11} fill="#768c9e" fontSize={9} fontWeight={600}>
                    {block.label}
                  </text>

                  {block.weeks.map((col, colIdx) =>
                    col.map((day, row) => {
                      if (!day) return null
                      const ds = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`
                      const count = activityMap.get(ds) ?? 0
                      return (
                        <rect
                          key={ds}
                          x={blockX + colIdx * STEP}
                          y={16 + row * STEP}
                          width={CELL}
                          height={CELL}
                          rx={2}
                          fill={getColor(count)}
                        >
                          <title>{`${ds}: ${count} ${t('profile.activities')}`}</title>
                        </rect>
                      )
                    })
                  )}
                </g>
              )
            })
          })()}
        </svg>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-end gap-1 mt-2 text-[10px] text-text-hint">
        <span>{t('profile.less')}</span>
        {INTENSITY_COLORS.map((color, i) => (
          <div key={i} className="w-[10px] h-[10px] rounded-sm" style={{ backgroundColor: color }} />
        ))}
        <span>{t('profile.more')}</span>
      </div>
    </div>
  )
}
